import { supabase } from './supabaseClient';
import type { User } from '@supabase/supabase-js';

/**
 * OAuth metadata from providers (Google, Apple)
 */
interface OAuthMetadata {
  // Google fields
  given_name?: string;
  family_name?: string;
  picture?: string;
  
  // Apple fields
  name?: string;
  
  // Common fields
  email?: string;
  avatar_url?: string;
  full_name?: string;
}

/**
 * Extract name components from OAuth metadata
 */
function extractNameFromMetadata(metadata: OAuthMetadata): { firstName: string; lastName: string } {
  let firstName = '';
  let lastName = '';

  // Try Google fields first
  if (metadata.given_name) {
    firstName = metadata.given_name;
  }
  if (metadata.family_name) {
    lastName = metadata.family_name;
  }

  // If no Google fields, try to parse full name or name field
  if (!firstName && !lastName) {
    const fullName = metadata.full_name || metadata.name || '';
    const nameParts = fullName.trim().split(' ');
    
    if (nameParts.length > 0) {
      firstName = nameParts[0];
      if (nameParts.length > 1) {
        lastName = nameParts.slice(1).join(' ');
      }
    }
  }

  // Fallback to email username if no name available
  if (!firstName && metadata.email) {
    firstName = metadata.email.split('@')[0];
  }

  return { firstName, lastName };
}

/**
 * Check if a profile exists for the given user
 */
export async function checkProfileExists(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();

    if (error) {
      // PGRST116 means no rows returned (profile doesn't exist)
      if (error.code === 'PGRST116') {
        return false;
      }
      throw error;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking profile existence:', error);
    return false;
  }
}

/**
 * Create a profile for an OAuth user
 */
export async function createOAuthProfile(user: User, metadata?: OAuthMetadata): Promise<void> {
  try {
    // Check if profile already exists
    const profileExists = await checkProfileExists(user.id);
    if (profileExists) {
      console.log('Profile already exists for user:', user.id);
      return;
    }

    // Extract metadata from user object or provided metadata
    const userMetadata = metadata || (user.user_metadata as OAuthMetadata) || {};
    
    // Extract name components
    const { firstName, lastName } = extractNameFromMetadata(userMetadata);

    // Create profile with OAuth data (email is required by DB; fallback for edge cases e.g. Apple Hide My Email)
    const email = user.email ?? userMetadata.email ?? '';
    const { error: insertError } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        email,
        first_name: firstName || 'Usuario',
        last_name: lastName || 'OAuth',
        role: 'lawyer', // Default role for OAuth users
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

    if (insertError) {
      // If error is duplicate key, profile was created by trigger - that's OK
      if (insertError.code === '23505') {
        console.log('Profile already created by database trigger');
        return;
      }
      throw insertError;
    }

    console.log('OAuth profile created successfully for user:', user.id);
  } catch (error) {
    console.error('Error creating OAuth profile:', error);
    throw error;
  }
}

/**
 * Update existing profile with OAuth metadata
 * Useful when user links OAuth account to existing email/password account
 */
export async function updateProfileWithOAuthData(
  userId: string,
  metadata: OAuthMetadata
): Promise<void> {
  try {
    const { firstName, lastName } = extractNameFromMetadata(metadata);

    // Only update fields that are not already set
    const updates: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };

    // Check current profile data (avatar_url not in schema - profiles table has no avatar column)
    const { data: currentProfile, error: selectError } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', userId)
      .single();

    if (!selectError && currentProfile) {
      // Only update if current value is empty
      if (!currentProfile.first_name && firstName) {
        updates.first_name = firstName;
      }
      if (!currentProfile.last_name && lastName) {
        updates.last_name = lastName;
      }
    }

    // Only update if there are changes
    if (Object.keys(updates).length > 1) {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (error) {
        throw error;
      }

      console.log('Profile updated with OAuth data for user:', userId);
    }
  } catch (error) {
    console.error('Error updating profile with OAuth data:', error);
    throw error;
  }
}

/**
 * Handle OAuth user profile creation or update
 * This is the main function to call after OAuth authentication
 */
export async function handleOAuthProfile(user: User): Promise<void> {
  try {
    const profileExists = await checkProfileExists(user.id);

    if (profileExists) {
      // Profile exists - update with OAuth data if needed
      await updateProfileWithOAuthData(user.id, user.user_metadata as OAuthMetadata);
    } else {
      // Profile doesn't exist - create new one
      await createOAuthProfile(user, user.user_metadata as OAuthMetadata);
    }
  } catch (error) {
    console.error('Error handling OAuth profile:', error);
    throw error;
  }
}

export const oauthProfileService = {
  checkProfileExists,
  createOAuthProfile,
  updateProfileWithOAuthData,
  handleOAuthProfile,
};
